import React, { useEffect, useRef, useState } from 'react';
import './WorkplaceSimDashboard.css';

// Polls the one public endpoint the backend exposes for this:
//   GET /api/workplace/state -> { game, org_chart, news }
// Point this at your deployed API host.
const API_BASE = 'https://forteindiedev.com';
const POLL_MS = 4000;
const TOAST_LIFESPAN_MS = 6000;

const DEPARTMENT_ORDER = ['Sales', 'Engineering', 'Marketing', 'Finance', 'Operations'];
// Tiers come back ordered VP(6) -> Intern(1) from the API; render top-down that way.

const DEPARTMENT_THEME = {
    Sales: 'dept-sales',
    Engineering: 'dept-engineering',
    Marketing: 'dept-marketing',
    Finance: 'dept-finance',
    Operations: 'dept-operations',
};

// Viewer/trainer reference only — agents are expected to already know this
// from their own prompting, this table is purely for whoever's watching.
const ACTION_REFERENCE = [
    { name: 'work', ap: '1-10', note: 'Commit AP to your assigned task. Diminishing returns — spreading AP across a few things beats dumping it all in one place.' },
    { name: 'endorse', ap: 1, note: 'Small cooperation boost to a colleague. No exposure risk — this is just being nice, publicly.' },
    { name: 'recommend', ap: 2, note: "Connections boost to a colleague, weighted by YOUR OWN tier — a VP's word counts far more than an Intern's." },
    { name: 'alliance_propose / alliance_accept', ap: 1, note: 'Form a standing alliance. Both parties get a small cooperation bonus every round it stays active — until someone breaks it.' },
    { name: 'withhold', ap: 0, note: "Quietly refuse to cooperate on something. A small dent to the target's cooperation score. An omission, not a lie — never exposable." },
    { name: 'badmouth', ap: 2, note: "Cooperation penalty to a target. Sabotage — carries exposure risk if it's not your only political move this round." },
    { name: 'undermine', ap: 2, note: "Steal a slice of a target's income score for the round into your own. Sabotage — same exposure risk as badmouth." },
    { name: 'champion', ap: 1, note: 'Vote to replace the sitting CEO with your candidate, weighted by your own tier. The ONLY way the CEO seat ever changes hands.' },
    { name: 'cross_department_bid', ap: 3, note: 'Bid for an open VP seat in another department. Locked until the late-game unlock round.' },
];

// Polls /api/workplace/state and, on top of returning the latest snapshot,
// diffs each poll's news list against what's already been seen so the UI can
// animate ONLY the events that are actually new — not replay the whole feed
// every time it reloads. The very first poll marks everything already there
// as "seen" without animating it (so loading the page mid-event doesn't fire
// a burst of toasts for old news).
function useWorkplaceState() {
    const [state, setState] = useState(null);
    const [error, setError] = useState(null);
    const [newEvents, setNewEvents] = useState([]);
    const seenIds = useRef(new Set());
    const initialized = useRef(false);
    const timerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        async function poll() {
            try {
                const res = await fetch(`${API_BASE}/api/workplace/state`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) {
                    const news = data.news || [];
                    if (!initialized.current) {
                        news.forEach((n) => seenIds.current.add(n.id));
                        initialized.current = true;
                    } else {
                        const fresh = news.filter((n) => !seenIds.current.has(n.id));
                        news.forEach((n) => seenIds.current.add(n.id));
                        if (fresh.length > 0) setNewEvents(fresh);
                    }
                    setState(data);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) timerRef.current = setTimeout(poll, POLL_MS);
            }
        }
        poll();
        return () => {
            cancelled = true;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return { state, error, newEvents };
}

// Turns newly-seen news items into a live toast queue (auto-expiring) and
// the set of agent ids currently involved in one, so the org chart can pulse
// the right seat card(s) at the same time the toast is showing.
function useLiveEventToasts(newEvents) {
    const [toasts, setToasts] = useState([]);
    const toastCounter = useRef(0);

    useEffect(() => {
        if (newEvents.length === 0) return;
        const now = Date.now();
        const added = newEvents.map((item) => ({
            key: `${item.id}-${toastCounter.current++}`,
            item,
            expiresAt: now + TOAST_LIFESPAN_MS,
        }));
        setToasts((prev) => [...prev, ...added]);
    }, [newEvents]);

    useEffect(() => {
        if (toasts.length === 0) return undefined;
        const id = setInterval(() => {
            setToasts((prev) => prev.filter((t) => t.expiresAt > Date.now()));
        }, 500);
        return () => clearInterval(id);
    }, [toasts]);

    const pulsingAgentIds = new Set();
    for (const t of toasts) {
        if (t.item.agent_id) pulsingAgentIds.add(t.item.agent_id);
        if (t.item.other_agent_id) pulsingAgentIds.add(t.item.other_agent_id);
    }

    return { toasts, pulsingAgentIds };
}

function formatClock(seconds) {
    if (seconds === null || seconds === undefined) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

// The server only tells us seconds_remaining once per poll (every 4s), which
// looks choppy on screen. This ticks a LOCAL copy down every second between
// polls. ONE interval runs for the component's entire lifetime (never torn
// down and recreated on phase/round/pause changes — that restart dance was
// the likely source of the choppiness) and always reads the latest server
// anchor through a ref, which a separate effect just mutates in place
// whenever a new poll arrives. The tick math is based on absolute elapsed
// wall-clock time (now - syncedAt), not an incrementing counter, so it can't
// drift even if a tick fires a little early or late.
function useTickingSeconds(game) {
    const [displaySeconds, setDisplaySeconds] = useState(null);
    const anchorRef = useRef({ seconds: null, syncedAt: Date.now(), paused: false });

    // Keep the anchor fresh — this effect's only job is to update the ref
    // and snap the displayed value to the server's truth. It never touches
    // the interval below.
    useEffect(() => {
        if (!game) return;
        anchorRef.current = {
            seconds: game.seconds_remaining,
            syncedAt: Date.now(),
            paused: Boolean(game.paused),
        };
        setDisplaySeconds(game.seconds_remaining);
    }, [game && game.seconds_remaining, game && game.paused, game && game.phase, game && game.round_number]);

    // One persistent interval for the whole component lifetime.
    useEffect(() => {
        const id = setInterval(() => {
            const a = anchorRef.current;
            if (a.seconds === null || a.seconds === undefined || a.paused) return;
            const elapsed = Math.floor((Date.now() - a.syncedAt) / 1000);
            setDisplaySeconds(Math.max(0, a.seconds - elapsed));
        }, 1000);
        return () => clearInterval(id);
    }, []);

    return displaySeconds;
}

function PhaseBanner({ game }) {
    const tickingSeconds = useTickingSeconds(game);
    if (!game) return null;
    const { phase, round_number, total_rounds, paused, cross_department_unlocked } = game;

    const phaseLabel = {
        not_started: 'NOT STARTED',
        working: 'WORKING HOURS',
        review: 'PERFORMANCE REVIEW',
        finished: 'FINAL REVIEW COMPLETE',
    }[phase] || phase.toUpperCase();

    return (
        <div className={`phase-banner phase-${phase}`}>
            <div className="phase-banner-left">
                <span className="phase-label">{phaseLabel}</span>
                {paused && <span className="paused-chip">PAUSED</span>}
            </div>
            <div className="phase-banner-center">
                <span className="round-chip">Round {round_number || 0} / {total_rounds}</span>
            </div>
            <div className="phase-banner-right">
                <span className="clock">{formatClock(tickingSeconds)}</span>
            </div>
            {cross_department_unlocked && (
                <div className="unlock-ribbon">Cross-department moves are now open — VP seats across the company are in play.</div>
            )}
        </div>
    );
}

const AP_BUDGET_PER_ROUND = 10;

function ApChip({ apRemaining }) {
    if (apRemaining === null || apRemaining === undefined) return null;
    const used = Math.max(0, AP_BUDGET_PER_ROUND - apRemaining);
    const level = apRemaining <= 2 ? 'ap-low' : used > 0 ? 'ap-active' : 'ap-fresh';
    return (
        <span className={`seat-ap-chip ${level}`} title={`${used} AP spent this round`}>
            {apRemaining}/{AP_BUDGET_PER_ROUND} AP
        </span>
    );
}

function SeatCard({ seat, pulsing }) {
    const occupied = Boolean(seat.agent_id);
    const classes = ['seat-card', occupied ? (seat.is_npc ? 'seat-npc' : 'seat-player') : 'seat-empty'];
    if (pulsing) classes.push('seat-pulsing');
    return (
        <div className={classes.join(' ')}>
            <div className="seat-tier-label">{seat.tier}</div>
            <div className="seat-name">{occupied ? seat.display_name : 'Vacant'}</div>
            {occupied && (
                <div className="seat-meta">
                    <span className="seat-badge">{seat.is_npc ? 'NPC' : 'Player'}</span>
                    <span className="seat-score">{Number(seat.cumulative_score).toFixed(0)} pts</span>
                    <ApChip apRemaining={seat.ap_remaining} />
                </div>
            )}
        </div>
    );
}

function CeoSeat({ seat, pulsing }) {
    if (!seat) return null;
    const occupied = Boolean(seat.agent_id);
    const classes = ['ceo-card', occupied ? (seat.is_npc ? 'seat-npc' : 'seat-player') : 'seat-empty'];
    if (pulsing) classes.push('seat-pulsing');
    return (
        <div className="ceo-office">
            <div className="ceo-plaque">CORNER OFFICE</div>
            <div className={classes.join(' ')}>
                <div className="ceo-name">{occupied ? seat.display_name : 'Vacant'}</div>
                {occupied && (
                    <div className="seat-meta">
                        <span className="seat-badge">{seat.is_npc ? 'NPC' : 'Player'}</span>
                        <span className="seat-score">{Number(seat.cumulative_score).toFixed(0)} pts</span>
                        <ApChip apRemaining={seat.ap_remaining} />
                    </div>
                )}
            </div>
            <div className="ceo-sub">Only a successful <code>champion</code> vote can unseat this person.</div>
        </div>
    );
}

function OrgChart({ orgChart, pulsingAgentIds }) {
    if (!orgChart) return null;
    const ceoSeat = orgChart.find((s) => s.is_ceo);
    const byDept = {};
    for (const seat of orgChart) {
        if (seat.is_ceo) continue;
        byDept[seat.department] = byDept[seat.department] || [];
        byDept[seat.department].push(seat);
    }
    for (const dept of Object.keys(byDept)) {
        byDept[dept].sort((a, b) => b.rank_order - a.rank_order); // VP at top, Intern at bottom
    }

    return (
        <div className="org-chart">
            <CeoSeat seat={ceoSeat} pulsing={ceoSeat && pulsingAgentIds.has(ceoSeat.agent_id)} />
            <div className="departments-row">
                {DEPARTMENT_ORDER.filter((d) => byDept[d]).map((dept) => (
                    <div key={dept} className={`department-column ${DEPARTMENT_THEME[dept] || ''}`}>
                        <div className="department-header">{dept}</div>
                        {byDept[dept].map((seat) => (
                            <SeatCard key={seat.seat_id} seat={seat} pulsing={pulsingAgentIds.has(seat.agent_id)} />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

const CATEGORY_ICON = {
    promotion: '⬆', // up arrow
    demotion: '⬇',
    exposure: '👀', // eyes
    coup: '👑', // crown
    alliance: '🤝', // handshake
    flavor: '📝',
};

function NewsFeed({ news }) {
    return (
        <div className="news-feed">
            <div className="panel-title">Company Updates</div>
            <div className="news-scroll">
                {(!news || news.length === 0) && <div className="news-empty">Nothing's happened yet. Give it a round.</div>}
                {news && news.map((item, i) => (
                    <div key={i} className={`news-item news-${item.category || 'flavor'}`}>
                        <span className="news-icon">{CATEGORY_ICON[item.category] || '📝'}</span>
                        <div className="news-text">
                            <div className="news-headline">{item.headline}</div>
                            {item.body && <div className="news-body">{item.body}</div>}
                            <div className="news-round">Round {item.round_number}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// A floating "?" button + full-screen modal, instead of a cramped side
// panel, so the handbook text can actually be read at a glance from across
// a room (projector use case) rather than squinting at a sidebar table.
function HandbookButton({ open, onOpen }) {
    if (open) return null;
    return (
        <button type="button" className="handbook-fab" onClick={onOpen} aria-label="Open the employee handbook" title="Employee handbook">
            ?
        </button>
    );
}

function HandbookModal({ open, onClose }) {
    if (!open) return null;
    return (
        <div className="handbook-overlay" onClick={onClose}>
            <div className="handbook-modal" onClick={(e) => e.stopPropagation()}>
                <div className="handbook-modal-header">
                    <div className="panel-title">Employee Handbook (viewers only)</div>
                    <button type="button" className="handbook-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Action</th>
                            <th>AP</th>
                            <th>What it does</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ACTION_REFERENCE.map((a) => (
                            <tr key={a.name}>
                                <td className="action-name">{a.name}</td>
                                <td className="action-ap">{a.ap}</td>
                                <td className="action-note">{a.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="handbook-footnote">Every agent has 10 AP per round, shared between work and all of the above.</div>
            </div>
        </div>
    );
}

// Live, on-screen "this just happened" callouts — stacked top-right, each
// auto-expiring. This is the piece that replaces events quietly appearing
// only in the Water Cooler list.
function ToastStack({ toasts }) {
    if (toasts.length === 0) return null;
    return (
        <div className="toast-stack">
            {toasts.map((t) => (
                <div key={t.key} className={`toast news-${t.item.category || 'flavor'}`}>
                    <span className="toast-icon">{CATEGORY_ICON[t.item.category] || '📝'}</span>
                    <div className="toast-text">
                        <div className="toast-headline">{t.item.headline}</div>
                        {t.item.body && <div className="toast-body">{t.item.body}</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function WorkplaceSimDashboard() {
    const { state, error, newEvents } = useWorkplaceState();
    const { toasts, pulsingAgentIds } = useLiveEventToasts(newEvents);
    const [handbookOpen, setHandbookOpen] = useState(false);

    return (
        <div className="workplace-dashboard">
            <div className="masthead">
                <div className="masthead-title">ORGCHART.LIVE</div>
                <div className="masthead-sub">Real-time company status</div>
            </div>

            <PhaseBanner game={state?.game} />

            {error && <div className="connection-error">Can't reach the office right now ({error}) — retrying...</div>}

            <div className="dashboard-body">
                <OrgChart orgChart={state?.org_chart} pulsingAgentIds={pulsingAgentIds} />
                <div className="side-column">
                    <NewsFeed news={state?.news} />
                </div>
            </div>

            <ToastStack toasts={toasts} />
            <HandbookButton open={handbookOpen} onOpen={() => setHandbookOpen(true)} />
            <HandbookModal open={handbookOpen} onClose={() => setHandbookOpen(false)} />
        </div>
    );
}